import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import ActivityStrip from './ActivityStrip';
import CostsTab from './CostsTab';
import PublishingTab from './PublishingTab';
import FormatLibrary from './FormatLibrary';
import GenerationOutput from './GenerationOutput';
import KeywordManager from './KeywordManager';
import ProductCatalog from './ProductCatalog';
import PromptEditor from './PromptEditor';
import StaticLibrary from './StaticLibrary';

// Mirrors Video Creative's tab set, minus the parts that only make sense for
// video (Creators — a static is generated, not filmed).
const TAB_ROUTES = [
  { path: 'library', label: 'Library', component: StaticLibrary },
  { path: 'formats', label: 'Formats', component: FormatLibrary },
  { path: 'output', label: 'Output', component: GenerationOutput },
  { path: 'products', label: 'Products', component: ProductCatalog },
  { path: 'keywords', label: 'Keywords', component: KeywordManager },
  { path: 'prompts', label: 'Prompts', component: PromptEditor },
  { path: 'publishing', label: 'Publishing', component: PublishingTab },
  { path: 'costs', label: 'Costs', component: CostsTab },
];

/**
 * Static Creative — the image-ad counterpart to Video Creative.
 *
 * A separate page rather than a seventh tab on /ad-library: that page already
 * keeps six tabs permanently mounted, and this feature brings its own set.
 *
 * Tabs are lazy-mounted on first visit and then kept mounted (hidden via
 * display:none), so filters, scroll position and loaded pages survive switching
 * — the same trade the Video Creative page makes.
 */
export default function StaticAdsPage() {
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const activeIndex = TAB_ROUTES.findIndex(t => location.pathname.startsWith(`${url}/${t.path}`));
  const active = activeIndex === -1 ? 0 : activeIndex;

  useEffect(() => {
    if (activeIndex === -1 && (location.pathname === url || location.pathname === `${url}/`)) {
      history.replace(`${url}/${TAB_ROUTES[0].path}`);
    }
  }, [activeIndex, location.pathname, url, history]);

  const [mounted, setMounted] = useState<Set<number>>(() => new Set([active]));
  useEffect(() => {
    setMounted(prev => (prev.has(active) ? prev : new Set([...prev, active])));
  }, [active]);

  return (
    <Box sx={{ minWidth: '80vw', width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" fontWeight={600}>Static Creative</Typography>
      {TAB_ROUTES.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tabs value={active} sx={{ flex: 1, minHeight: 48 }}>
            {TAB_ROUTES.map(t => (
              <Tab key={t.path} label={t.label} component={Link} to={`${url}/${t.path}`} />
            ))}
          </Tabs>
          <ActivityStrip />
        </Box>
      )}
      <Box sx={{ mt: 1 }}>
        {TAB_ROUTES.map((t, i) => {
          if (!mounted.has(i)) return null;
          const Comp = t.component;
          return (
            <Box key={t.path} sx={{ display: i === active ? 'block' : 'none' }}>
              <Comp />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
